/**
 * Fix free-shipping threshold using each message's own data-limit.
 *
 * Theme JS:
 * 1) Reads the FIRST [data-cart-message] limit (often announcement at 0)
 * 2) Multiplies by Shopify.currency.rate, which breaks Markets/presentment
 *    (e.g. limit 100 becomes ~€9 when shop is SEK → false "unlocked")
 *
 * Liquid compares `limit * 100` to `cart.total_price` (presentment cents)
 * with no rate multiply — we mirror that here.
 */
(function () {
  const SELECTORS = {
    message: '[data-cart-message]',
    messageDefault: '[data-message-default]',
    progress: '[data-cart-message-progress]',
    leftToSpend: '[data-left-to-spend]',
    cartTotal: '[data-cart-total]',
  };

  let scheduled = false;

  function getSubtotalCents() {
    // Prefer the cart drawer / cart form total if present
    const drawer = document.querySelector('[data-cart-drawer] [data-cart-total]');
    const el = drawer || document.querySelector(SELECTORS.cartTotal);
    if (!el) return 0;
    return Number(el.getAttribute('data-cart-total')) || 0;
  }

  function getThresholdCents(message) {
    const limitUnits = Number(message.getAttribute('data-limit'));
    if (!Number.isFinite(limitUnits) || limitUnits <= 0) return 0;
    // Match Liquid: limit units × 100 vs presentment cart.total_price
    return limitUnits * 100;
  }

  function formatMoney(cents) {
    const amount = Math.max(0, Math.round(cents));
    if (window.themeCurrency?.formatMoney) {
      const format = window.theme?.settings?.currency_code_enable
        ? window.theme.moneyWithCurrencyFormat
        : window.theme.moneyFormat;
      return window.themeCurrency
        .formatMoney(amount, format)
        .replace('.00', '')
        .replace(',00', '');
    }
    return String(amount / 100);
  }

  function syncMessage(message, subtotal) {
    const threshold = getThresholdCents(message);
    const hasQualifiedCopy =
      message.getAttribute('data-cart-message') === 'true' && subtotal !== 0;
    const defaultMessage = message.querySelector(SELECTORS.messageDefault);
    const progressItems = message.querySelectorAll(SELECTORS.progress);
    const leftToSpend = message.querySelector(SELECTORS.leftToSpend);

    if (threshold <= 0) {
      message.classList.remove('is-success');
      message.classList.toggle('is-hidden', subtotal === 0);
      defaultMessage?.classList.remove('is-hidden');
      progressItems.forEach((bar) => {
        bar.classList.remove('is-hidden');
        bar.style.setProperty('--progress-width', '0%');
        if (bar.hasAttribute('value')) bar.setAttribute('value', '0');
      });
      return;
    }

    const unlocked = hasQualifiedCopy && subtotal >= threshold;
    const percent = Math.min(100, Math.max(0, (subtotal / threshold) * 100));

    message.classList.toggle('is-success', unlocked);
    message.classList.toggle('is-hidden', subtotal === 0);

    if (defaultMessage) {
      // Show "spend more" whenever not unlocked
      defaultMessage.classList.toggle('is-hidden', unlocked);
    }

    if (leftToSpend && !unlocked) {
      leftToSpend.innerHTML = formatMoney(threshold - subtotal);
    }

    progressItems.forEach((bar, index) => {
      bar.classList.remove('is-hidden');
      bar.style.setProperty('--progress-width', `${percent}%`);
      if (index === 0) bar.setAttribute('value', String(percent));
    });
  }

  function syncFreeShipping() {
    const subtotal = getSubtotalCents();
    document.querySelectorAll(SELECTORS.message).forEach((message) => {
      syncMessage(message, subtotal);
    });
  }

  function scheduleSync() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      syncFreeShipping();
      // Theme updateProgress runs in the same turn — re-apply after it
      setTimeout(() => {
        syncFreeShipping();
        scheduled = false;
      }, 0);
      setTimeout(syncFreeShipping, 100);
    });
  }

  // Keep correcting if theme JS re-applies the wrong unlocked state
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'class' &&
        mutation.target.matches?.(SELECTORS.message)
      ) {
        scheduleSync();
        break;
      }
    }
  });

  function watchMessages() {
    document.querySelectorAll(SELECTORS.message).forEach((message) => {
      observer.observe(message, { attributes: true, attributeFilter: ['class'] });
    });
  }

  document.addEventListener('theme:cart:loaded', () => {
    watchMessages();
    scheduleSync();
  });
  document.addEventListener('theme:cart:added', scheduleSync);
  document.addEventListener('theme:cart:open', scheduleSync);
  document.addEventListener('theme:cart:update', scheduleSync);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      watchMessages();
      scheduleSync();
    });
  } else {
    watchMessages();
    scheduleSync();
  }
})();
